import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import * as userService from '../../services/userService';
import PrepaidUserDashboard from './PrepaidUserDashboard';
import PostpaidUserDashboard from './PostpaidUserDashboard';
import { Button } from '../../components/common';
import ButtonExamples from '../../components/common/ButtonExamples';

function UserDashboard() {
  const { user, unit: authUnit, hasRole, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isPrepaid, setIsPrepaid] = useState(null);
  const [error, setError] = useState(null);
  const [showButtonExamples, setShowButtonExamples] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if user has any staff role
  const isStaff = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));

  // Redirect staff users directly to admin dashboard
  if (user && isStaff && !authLoading) {
    return <Navigate to="/admin" replace />;
  }

  // Determine if user has a prepaid or postpaid unit
  useEffect(() => {
    const determineUnitType = async () => {
      // If we already know the type or the user isn't authenticated yet, skip
      if (isPrepaid !== null || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Try to get unit info from auth context first
        if (authUnit && authUnit.isPrepaid !== undefined) {
          // console.log('Using unit info from auth context:', authUnit);
          setIsPrepaid(authUnit.isPrepaid);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch from API
        if (user.siteUuid && user.unitId) {
          // console.log(`Fetching unit info for site: ${user.siteUuid}, unit: ${user.unitId}`);
          const unitInfo = await userService.getUnitByUnitId(user.siteUuid, user.unitId);
          
          if (unitInfo) {
            // console.log('Unit information fetched:', unitInfo);
            setIsPrepaid(unitInfo.isPrepaid);
          } else {
            setError('Could not determine unit type');
          }
        } else {
          setError('Missing site or unit information');
        }
      } catch (error) {
        // console.error('Failed to determine unit type:', error);
        setError('Error determining unit type');
      } finally {
        setLoading(false);
      }
    };

    determineUnitType();
  }, [user, authUnit, isPrepaid]);

  // Show loading indicator if auth is still loading
  if (authLoading || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading dashboard...</div>
      </div>
    );
  }

  // Show error message if we couldn't determine the unit type
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <div className="space-y-4">
          <Button 
            variant="primary"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>
          
          <Button 
            variant="secondary"
            onClick={() => setShowButtonExamples(!showButtonExamples)}
          >
            {showButtonExamples ? 'Hide' : 'Show'} Button Examples
          </Button>
          
          {showButtonExamples && (
            <div className="mt-8 max-w-3xl mx-auto">
              <ButtonExamples />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render the appropriate dashboard based on unit type
  if (isPrepaid === true) {
    return <PrepaidUserDashboard />;
  } else if (isPrepaid === false) {
    return <PostpaidUserDashboard />;
  }

  // Fallback if we somehow get here
  return <Navigate to="/" replace />;
}

export default UserDashboard; 